import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Users, Eye, Clock, MousePointer, FileText, Briefcase } from 'lucide-react';

const PlatformAnalyticsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/admin/platform-analytics`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          setError('Access denied. This page is only accessible to the platform owner.');
        } else if (response.status === 401) {
          setError('Authentication required. Please log in.');
        } else {
          setError(`Failed to load analytics (${response.status})`);
        }
        setLoading(false);
        return;
      }

      const data = await response.json();
      setAnalytics(data);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch platform analytics:', error);
      setError('Failed to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getAuthToken = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user?.tokens?.idToken || user?.idToken || '';
    } catch {
      return '';
    }
  };

  const formatTime = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${Math.round(ms / 1000)}s`;
    return `${Math.round(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading platform analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="text-red-600 text-xl font-semibold mb-2">Error</div>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={fetchAnalytics}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-700">No analytics data available</p>
        </div>
      </div>
    );
  }

  const { core_metrics, engagement_metrics, interaction_metrics, metadata } = analytics;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2">Platform Analytics</h1>
          <p className="text-gray-600">
            Aggregate usage metrics (last {metadata.date_range_days} days) · 
            Excluding: {metadata.excluded_email}
          </p>
        </div>
        <Link
          to="/admin/registered-users"
          className="btn btn-primary inline-flex items-center gap-2"
        >
          <Users className="w-5 h-5" />
          Registered User Accounts
        </Link>
      </div>

      {/* Core Experiment Metrics */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Users className="w-5 h-5 mr-2" />
          Core Experiment Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <MetricCard
            icon={<FileText className="w-6 h-6 text-blue-600" />}
            label="Visited Resume Builder"
            value={core_metrics.total_users_visited_resume}
            suffix="users"
          />
          <MetricCard
            icon={<Briefcase className="w-6 h-6 text-purple-600" />}
            label="Visited Portfolio"
            value={core_metrics.total_users_visited_portfolio}
            suffix="users"
          />
          <MetricCard
            icon={<BarChart3 className="w-6 h-6 text-green-600" />}
            label="Resume Only"
            value={core_metrics.percentage_resume_only}
            suffix="%"
            subtext="of total users"
          />
          <MetricCard
            icon={<BarChart3 className="w-6 h-6 text-orange-600" />}
            label="Portfolio At Least Once"
            value={core_metrics.percentage_portfolio_at_least_once}
            suffix="%"
            subtext="of total users"
          />
        </div>
      </div>

      {/* Engagement Metrics */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Eye className="w-5 h-5 mr-2" />
          Engagement Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <MetricCard
            icon={<FileText className="w-6 h-6 text-blue-600" />}
            label="Resume Page Views"
            value={engagement_metrics.resume_page_views}
            suffix="views"
          />
          <MetricCard
            icon={<Briefcase className="w-6 h-6 text-purple-600" />}
            label="Portfolio Page Views"
            value={engagement_metrics.portfolio_page_views}
            suffix="views"
          />
          <MetricCard
            icon={<Clock className="w-6 h-6 text-green-600" />}
            label="Portfolio Repeat Visits"
            value={engagement_metrics.portfolio_repeat_visits}
            suffix="visits"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <TimeMetricCard
            title="Resume Pages"
            avgTotal={formatTime(engagement_metrics.avg_time_resume_ms)}
            avgActive={formatTime(engagement_metrics.avg_active_time_resume_ms)}
          />
          <TimeMetricCard
            title="Portfolio Pages"
            avgTotal={formatTime(engagement_metrics.avg_time_portfolio_ms)}
            avgActive={formatTime(engagement_metrics.avg_active_time_portfolio_ms)}
          />
        </div>
      </div>

      {/* Interaction Metrics */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <MousePointer className="w-5 h-5 mr-2" />
          Interaction Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <MetricCard
            icon={<MousePointer className="w-6 h-6 text-blue-600" />}
            label="Portfolio Clicks"
            value={interaction_metrics.portfolio_clicks}
            suffix="clicks"
          />
          <MetricCard
            icon={<Briefcase className="w-6 h-6 text-purple-600" />}
            label="Created Portfolio Entry"
            value={interaction_metrics.users_created_portfolio_entry}
            suffix="users"
          />
          <MetricCard
            icon={<FileText className="w-6 h-6 text-green-600" />}
            label="Google Sites Integrations"
            value={interaction_metrics.google_sites_integrations}
            suffix="integrations"
          />
        </div>
      </div>

      {/* Metadata */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-600">
          Total tracked events: {metadata.total_events.toLocaleString()}
        </p>
      </div>
    </div>
  );
};

// MetricCard Component
const MetricCard = ({ icon, label, value, suffix, subtext }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex items-center mb-3">
      {icon}
      <h3 className="ml-2 text-gray-500 text-sm font-medium">{label}</h3>
    </div>
    <div className="flex items-baseline">
      <p className="text-3xl font-bold">{value.toLocaleString()}</p>
      {suffix && <span className="ml-2 text-gray-500 text-sm">{suffix}</span>}
    </div>
    {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
  </div>
);

// TimeMetricCard Component
const TimeMetricCard = ({ title, avgTotal, avgActive }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-gray-500 text-sm font-medium mb-4 flex items-center">
      <Clock className="w-4 h-4 mr-2" />
      {title}
    </h3>
    <div className="space-y-3">
      <div>
        <p className="text-xs text-gray-500">Avg Total Time</p>
        <p className="text-2xl font-bold">{avgTotal}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">Avg Active Time</p>
        <p className="text-2xl font-bold text-green-600">{avgActive}</p>
      </div>
    </div>
  </div>
);

export default PlatformAnalyticsPage;
